import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AvailabilityCalendar = ({ onDateSelect, selectedCheckIn, selectedCheckOut }) => {
  // Initialize currentMonth based on selectedCheckIn or default to today
  const getInitialMonth = () => {
    if (selectedCheckIn) {
      const date = new Date(selectedCheckIn + "T00:00:00");
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    return new Date();
  };
  
  const [currentMonth, setCurrentMonth] = useState(getInitialMonth);
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [temporarilyLockedRanges, setTemporarilyLockedRanges] = useState([]);
  const [blockedDatesLoading, setBlockedDatesLoading] = useState(true);
  const [selectingCheckOut, setSelectingCheckOut] = useState(false);
  const [tempCheckIn, setTempCheckIn] = useState(selectedCheckIn || null);
  const [tempCheckOut, setTempCheckOut] = useState(selectedCheckOut || null);

  // Fetch ALL blocked dates and temporarily locked dates from backend
  useEffect(() => {
    const fetchAllDates = async () => {
      setBlockedDatesLoading(true);
      try {
        const [blockedRes, tempLockedRes] = await Promise.all([
          axios.get(`${API}/availability-calendar-public`),
          axios.get(`${API}/temporarily-locked-dates`)
        ]);
        setBlockedRanges(blockedRes.data || []);
        setTemporarilyLockedRanges(tempLockedRes.data || []);
      } catch (error) {
        console.error("Failed to fetch dates:", error);
      } finally {
        setBlockedDatesLoading(false);
      }
    };
    fetchAllDates();
  }, []);
  
  // Update currentMonth when selectedCheckIn changes (e.g., from parent form)
  useEffect(() => {
    if (selectedCheckIn) {
      const date = new Date(selectedCheckIn + "T00:00:00");
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  }, [selectedCheckIn]);

  // Sync with parent props
  useEffect(() => {
    if (selectedCheckIn) setTempCheckIn(selectedCheckIn);
    if (selectedCheckOut) setTempCheckOut(selectedCheckOut);
  }, [selectedCheckIn, selectedCheckOut]);

  // Check if a date is blocked (admin blocked - Pink/Booked)
  const isDateBlocked = useCallback((dateStr) => {
    for (const range of blockedRanges) {
      if (range.status === "BLOCKED") {
        if (dateStr >= range.start_date && dateStr <= range.end_date) {
          return true;
        }
      }
    }
    return false;
  }, [blockedRanges]);

  // Check if a date is temporarily locked by a customer (Orange)
  const isDateTemporarilyLocked = useCallback((dateStr) => {
    for (const range of temporarilyLockedRanges) {
      if (dateStr >= range.start_date && dateStr <= range.end_date) {
        return true;
      }
    }
    return false;
  }, [temporarilyLockedRanges]);

  // Check if date is in the past
  const isPastDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr + "T00:00:00");
    return date < today;
  };

  // Check if date is selected (in range)
  const isInSelectedRange = (dateStr) => {
    if (!tempCheckIn || !tempCheckOut) return false;
    return dateStr >= tempCheckIn && dateStr < tempCheckOut;
  };

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Handle date click
  const handleDateClick = (dateStr) => {
    if (isPastDate(dateStr) || isDateBlocked(dateStr)) return;

    if (!selectingCheckOut) {
      // Selecting check-in
      setTempCheckIn(dateStr);
      setTempCheckOut(null);
      setSelectingCheckOut(true);
    } else {
      // Selecting check-out
      if (dateStr <= tempCheckIn) {
        // If clicked date is before check-in, reset and set as new check-in
        setTempCheckIn(dateStr);
        setTempCheckOut(null);
      } else {
        // Check if any date in range is blocked
        let hasBlockedInRange = false;
        const checkInDate = new Date(tempCheckIn + "T00:00:00");
        const checkOutDate = new Date(dateStr + "T00:00:00");
        
        for (let d = new Date(checkInDate); d < checkOutDate; d.setDate(d.getDate() + 1)) {
          const dStr = d.toISOString().split("T")[0];
          if (isDateBlocked(dStr)) {
            hasBlockedInRange = true;
            break;
          }
        }

        if (hasBlockedInRange) {
          // Reset selection
          setTempCheckIn(dateStr);
          setTempCheckOut(null);
        } else {
          setTempCheckOut(dateStr);
          setSelectingCheckOut(false);
          // Notify parent
          if (onDateSelect) {
            onDateSelect(tempCheckIn, dateStr);
          }
        }
      }
    }
  };

  // Render calendar
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Day headers
    const headers = dayNames.map((day) => (
      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
        {day}
      </div>
    ));

    // Empty cells for days before first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const isBlocked = isDateBlocked(dateStr);
      const isTempLocked = isDateTemporarilyLocked(dateStr);
      const isPast = isPastDate(dateStr);
      const isCheckIn = tempCheckIn === dateStr;
      const isCheckOut = tempCheckOut === dateStr;
      const isInRange = isInSelectedRange(dateStr);
      const isToday = dateStr === new Date().toISOString().split("T")[0];
      const isDisabled = isPast || isBlocked || isTempLocked;

      let dayClass = "p-2 text-center text-sm rounded-lg cursor-pointer transition-all duration-200 ";
      let titleText = "Available";
      
      if (isBlocked && !isPast) {
        // Booked dates - Pink
        dayClass += "bg-pink-100 text-pink-600 line-through cursor-not-allowed font-medium ";
        titleText = "Booked";
      } else if (isTempLocked && !isPast) {
        // Temporarily locked by customer - Orange
        dayClass += "bg-orange-100 text-orange-600 cursor-not-allowed font-medium ";
        titleText = "Temporarily Blocked by Customer";
      } else if (isPast) {
        // Past dates - gray
        dayClass += "text-gray-300 cursor-not-allowed ";
        titleText = "Past date";
      } else if (isCheckIn) {
        // User's selected check-in - Brown
        dayClass += "bg-[#B08968] text-white font-bold ";
        titleText = "Your check-in";
      } else if (isCheckOut) {
        // User's selected check-out - Brown
        dayClass += "bg-[#9A7558] text-white font-bold ";
        titleText = "Your check-out";
      } else if (isInRange) {
        // User's selected range - Light brown
        dayClass += "bg-[#B08968]/20 text-[#B08968] ";
        titleText = "Your selection";
      } else {
        // Available - Green
        dayClass += "bg-green-50 text-green-700 hover:bg-green-100 ";
        if (isToday) {
          dayClass += "ring-2 ring-[#B08968] ";
        }
      }

      days.push(
        <div
          key={dateStr}
          className={dayClass}
          onClick={() => !isDisabled && handleDateClick(dateStr)}
          title={titleText}
        >
          {day}
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-7 gap-1">{headers}</div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </>
    );
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div className="bg-white rounded-xl border p-4 mt-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPreviousMonth}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <h3 className="font-medium text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={goToNextMonth}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Selection status */}
      <div className="text-center mb-3 text-sm">
        {!tempCheckIn && (
          <span className="text-gray-500">Select your check-in date</span>
        )}
        {tempCheckIn && !tempCheckOut && (
          <span className="text-[#B08968]">Now select your check-out date</span>
        )}
        {tempCheckIn && tempCheckOut && (
          <span className="text-green-600 font-medium">
            ✓ {new Date(tempCheckIn + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })} 
            {" → "} 
            {new Date(tempCheckOut + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
          </span>
        )}
      </div>

      {/* Calendar grid */}
      {blockedDatesLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#B08968]" />
          <span className="ml-2 text-sm text-gray-500">Loading availability...</span>
        </div>
      ) : (
        renderCalendar()
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-green-50 border border-green-200"></div>
          <span className="text-gray-500">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-pink-100 border border-pink-200"></div>
          <span className="text-gray-500">Booked</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-orange-100 border border-orange-200"></div>
          <span className="text-gray-500">Temp. Blocked</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-[#B08968]"></div>
          <span className="text-gray-500">Selected</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
